import { useEffect, useState } from "react";
import { api, apiError } from "../lib/apiClient";
import { toast } from "sonner";
import { CreditCard, Eye, EyeOff, Loader2, Save, RotateCcw, Plug } from "lucide-react";

const ORANGE = "#ff8a3d";
const FIELDS = [
  { id: "secret_key", label: "Clé secrète (STRIPE_SECRET_KEY)", placeholder: "sk_test_… ou sk_live_…" },
  { id: "publishable_key", label: "Clé publique (STRIPE_PUBLISHABLE_KEY)", placeholder: "pk_test_… ou pk_live_…" },
  { id: "webhook_secret", label: "Secret de webhook (STRIPE_WEBHOOK_SECRET)", placeholder: "whsec_…" },
];
const SOURCE_LABEL = { admin: "Admin", env: "Environnement", none: "Non définie" };

export function StripeKeysPanel() {
  const [cfg, setCfg] = useState(null);
  const [values, setValues] = useState({ secret_key: "", publishable_key: "", webhook_secret: "" });
  const [revealed, setRevealed] = useState(null);
  const [mode, setMode] = useState("test");
  const [busy, setBusy] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [saveError, setSaveError] = useState("");

  const load = () =>
    api
      .get("/admin/stripe/keys")
      .then(({ data }) => {
        setCfg(data);
        setMode(data.mode || "test");
      })
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    const payload = { mode };
    FIELDS.forEach((f) => {
      if (values[f.id].trim()) payload[f.id] = values[f.id].trim();
    });
    setBusy("save");
    setSaveError("");
    try {
      const { data } = await api.post("/admin/stripe/keys", payload);
      setCfg(data);
      setValues({ secret_key: "", publishable_key: "", webhook_secret: "" });
      setRevealed(null);
      toast.success("Clés Stripe enregistrées — appliquées immédiatement");
    } catch (e) {
      const msg = apiError(e, "Enregistrement impossible");
      setSaveError(msg);
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const reveal = async () => {
    if (revealed) return setRevealed(null);
    setBusy("reveal");
    try {
      const { data } = await api.get("/admin/stripe/keys/reveal");
      setRevealed(data.keys || {});
    } catch (e) {
      toast.error(apiError(e, "Lecture impossible"));
    } finally {
      setBusy(null);
    }
  };

  const testKeys = async () => {
    setBusy("test");
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/stripe/keys/test");
      setTestResult(data);
      if (data.ok) toast.success(`Clé valide — compte ${data.account_id}${data.livemode ? " (réel)" : " (test)"}`);
      else toast.error(data.error || "Clé refusée par Stripe");
    } catch (e) {
      toast.error(apiError(e, "Test impossible"));
    } finally {
      setBusy(null);
    }
  };

  const revertToEnv = async () => {
    if (!window.confirm("Revenir aux clés du serveur (.env) et effacer celles saisies ici ?")) return;
    setBusy("reset");
    try {
      const { data } = await api.delete("/admin/stripe/keys");
      setCfg(data);
      setMode(data.mode || "test");
      setRevealed(null);
      toast.success("Clés admin effacées — retour aux clés du serveur");
    } catch (e) {
      toast.error(apiError(e, "Réinitialisation impossible"));
    } finally {
      setBusy(null);
    }
  };

  if (!cfg) {
    return (
      <div data-testid="stripe-keys-panel" className="hud-panel clip-corner flex items-center justify-center p-8">
        <Loader2 className="h-5 w-5 animate-spin accent-text" />
      </div>
    );
  }

  return (
    <section
      data-testid="stripe-keys-panel"
      className="relative overflow-hidden rounded-2xl border p-5 sm:p-6"
      style={{
        borderColor: "rgba(255,138,61,0.28)",
        background:
          "linear-gradient(135deg, rgba(8,6,20,0.92), rgba(3,7,18,0.96)), radial-gradient(circle at 90% 5%, rgba(255,138,61,0.12), transparent 45%)",
      }}
    >
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span
            className="flex h-9 w-9 items-center justify-center rounded-lg"
            style={{ background: "rgba(255,138,61,0.14)", border: `1px solid ${ORANGE}` }}
          >
            <CreditCard className="h-4 w-4" style={{ color: ORANGE }} />
          </span>
          <div>
            <h3 className="font-display text-lg font-black tracking-[0.1em] text-white text-glow">CLÉS STRIPE</h3>
            <p className="font-mono-hud text-[10px] tracking-[0.18em] text-[#8b949e] uppercase">
              Modifiables ici — appliquées sans redémarrage
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5" data-testid="stripe-mode-switch">
          {["test", "live"].map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`stripe-mode-${m}`}
              onClick={() => setMode(m)}
              className={`clip-corner border px-3 py-1.5 font-mono-hud text-[10px] uppercase tracking-widest transition-colors ${
                mode === m ? "text-white" : "text-[#8b949e] hover:text-white"
              }`}
              style={mode === m ? { borderColor: ORANGE, background: "rgba(255,138,61,0.16)" } : { borderColor: "var(--border-hud)" }}
            >
              {m === "test" ? "Mode test" : "Mode réel"}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {FIELDS.map((f) => {
          const meta = cfg.keys?.[f.id] || {};
          return (
            <div key={f.id} data-testid={`stripe-key-${f.id}`}>
              <label
                className="mb-1.5 block font-mono-hud text-[9px] uppercase tracking-[0.2em]"
                style={{ color: ORANGE }}
              >
                {f.label}
              </label>
              <div className="mb-1.5 flex items-center gap-2 font-mono text-[11px] text-[#9fb2c6]">
                <code data-testid={`stripe-key-value-${f.id}`} className="min-w-0 flex-1 truncate">
                  {revealed ? revealed[f.id] || "—" : meta.masked || "—"}
                </code>
                <span className="shrink-0 font-mono-hud text-[9px] uppercase tracking-widest text-[#6b7a8d]">
                  {SOURCE_LABEL[meta.source] || "—"}
                </span>
              </div>
              <input
                data-testid={`stripe-key-input-${f.id}`}
                value={values[f.id]}
                onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
                placeholder={f.placeholder}
                spellCheck={false}
                className="hud-input font-mono text-[12px]"
              />
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2.5">
        <button
          type="button"
          data-testid="stripe-keys-save"
          onClick={save}
          disabled={busy === "save"}
          className="flex items-center gap-2 clip-corner px-4 py-2.5 text-[12px] font-bold text-black transition-transform hover:scale-[1.02] disabled:opacity-50"
          style={{ background: ORANGE }}
        >
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Enregistrer
        </button>
        <button
          type="button"
          data-testid="stripe-keys-reveal"
          onClick={reveal}
          disabled={busy === "reveal"}
          className="flex items-center gap-2 clip-corner border border-[var(--border-hud)] px-4 py-2.5 font-mono-hud text-[10px] uppercase tracking-widest text-[#8b949e] transition-colors hover:text-white disabled:opacity-50"
        >
          {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {revealed ? "Masquer" : "Afficher"}
        </button>
        <button
          type="button"
          data-testid="stripe-keys-test"
          onClick={testKeys}
          disabled={busy === "test"}
          className="flex items-center gap-2 clip-corner border border-[var(--accent)] px-4 py-2.5 font-mono-hud text-[10px] uppercase tracking-widest accent-text transition-colors hover:bg-[var(--accent-a10)] disabled:opacity-50"
        >
          {busy === "test" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-3.5 w-3.5" />}
          Tester la connexion
        </button>
        <button
          type="button"
          data-testid="stripe-keys-revert"
          onClick={revertToEnv}
          disabled={busy === "reset"}
          className="flex items-center gap-2 clip-corner border border-[#ff6b6b]/50 px-4 py-2.5 font-mono-hud text-[10px] uppercase tracking-widest text-[#ff6b6b] transition-colors hover:bg-[#ff6b6b]/10 disabled:opacity-50"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Clés du serveur
        </button>
      </div>

      {saveError && (
        <p data-testid="stripe-keys-save-error" className="mt-3 font-mono-hud text-[11px] text-[#ff6b6b]">
          » {saveError}
        </p>
      )}

      {testResult && (
        <p
          data-testid="stripe-keys-test-result"
          className="mt-3 font-mono-hud text-[11px]"
          style={{ color: testResult.ok ? "#00ffaa" : "#ff6b6b" }}
        >
          {testResult.ok
            ? `Connexion OK — ${testResult.account_id}${testResult.business_name ? ` · ${testResult.business_name}` : ""} · ${
                testResult.livemode ? "clés réelles" : "clés de test"
              }`
            : `Échec — ${testResult.error}`}
        </p>
      )}

      <p className="mt-3 font-mono-hud text-[10px] leading-relaxed text-[#6b7a8d]">
        Laisse un champ vide pour conserver la valeur actuelle. Les clés saisies ici sont stockées côté serveur et
        prennent le dessus sur le fichier .env.
        {cfg.updated_at ? ` Dernière modification : ${cfg.updated_at.slice(0, 16).replace("T", " ")} par ${cfg.updated_by || "admin"}.` : ""}
      </p>
    </section>
  );
}
